import readXlsxFile from 'read-excel-file/browser';

export interface ExcelImportColumn {
  key: string;
  label: string;
  required?: boolean;
  aliases?: string[];
  example?: string;
  fallback?: any;
  description?: string;
}

export interface ExcelImportRow {
  __rowNumber: number;
  __status: 'ready' | 'filled';
  __notes: string[];
  [key: string]: any;
}

export const catalogueProductImportSchema: ExcelImportColumn[] = [
  { key: 'sku', label: 'sku', required: true, aliases: ['SKU'], example: 'SKU-001', fallback: (rowNumber: number) => `DUMMY-SKU-${rowNumber}` },
  { key: 'name', label: 'name', required: true, aliases: ['productName', 'Product Name'], example: 'Argan Oil', fallback: (rowNumber: number) => `Dummy Product ${rowNumber}` },
  { key: 'description', label: 'description', example: 'Cold pressed oil' },
  { key: 'categoryName', label: 'categoryName', aliases: ['category', 'Category'], example: 'Oils' },
  { key: 'categoryId', label: 'categoryId', example: '12' },
  { key: 'supplierName', label: 'supplierName', aliases: ['supplier', 'Supplier'], example: 'ABC Supplier' },
  { key: 'supplierId', label: 'supplierId', example: '4' },
  { key: 'unit', label: 'unit', example: 'kg' },
  { key: 'isActive', label: 'isActive', example: 'TRUE', fallback: true },
  { key: 'minQuantity', label: 'minQuantity', example: '1' },
  { key: 'maxQuantity', label: 'maxQuantity', example: '100' },
  { key: 'pricePerUnit', label: 'pricePerUnit', example: '12.50' },
  { key: 'currency', label: 'currency', example: 'USD' },
];

export const supplierImportSchema: ExcelImportColumn[] = [
  { key: 'name', label: 'name', required: true, aliases: ['supplierName', 'Supplier Name'], example: 'ABC Supplier', fallback: (rowNumber: number) => `Dummy Supplier ${rowNumber}` },
  { key: 'type', label: 'type', required: true, example: 'MANUFACTURER, DISTRIBUTOR', fallback: 'MANUFACTURER', description: 'Select one or more comma-separated types: SUPPLIER, RAW_MATERIALS_SOURCING, MANUFACTURER, DISTRIBUTOR, WHOLESALER, SERVICE_PROVIDER, RAW_MATERIAL_SUPPLIER, PACKAGING_SUPPLIER, CONTRACT_MANUFACTURER, OIL_SUPPLIER, OTHER' },
  { key: 'customType', label: 'customType', example: 'Fragrance House' },
  { key: 'status', label: 'status', example: 'ACTIVE_SUPPLIER', fallback: 'ACTIVE_SUPPLIER' },
  { key: 'emails', label: 'emails', aliases: ['email'], example: 'sales@example.com; qc@example.com' },
  { key: 'phoneNumbers', label: 'phoneNumbers', aliases: ['phone'], example: '+1 555 0100; +1 555 0101' },
  { key: 'websites', label: 'websites', aliases: ['website'], example: 'https://example.com' },
  { key: 'address', label: 'address', example: '123 Market Street' },
  { key: 'importance', label: 'importance', example: 'MEDIUM', fallback: 'MEDIUM' },
  { key: 'notes', label: 'notes', example: 'Preferred vendor' },
  { key: 'contactName', label: 'contactName', example: 'Jane Smith' },
  { key: 'contactRole', label: 'contactRole', example: 'Sales Rep' },
  { key: 'contactEmail', label: 'contactEmail', example: 'jane@example.com' },
  { key: 'contactPhone', label: 'contactPhone', example: '+1 555 0199' },
];

export const supplierCatalogueProductImportSchema: ExcelImportColumn[] = [
  { key: 'type', label: 'type', required: true, example: 'Product', fallback: 'Product', description: 'Existing type name or legacy key. Unknown names will be created as new supplier product types.' },
  { key: 'sku', label: 'sku', aliases: ['SKU'], example: 'SUP-SKU-001' },
  { key: 'name', label: 'name', required: true, aliases: ['productName', 'Product Name'], example: 'Lavender Oil', fallback: (rowNumber: number) => `Dummy Supplier Product ${rowNumber}` },
  { key: 'categoryName', label: 'categoryName', required: true, aliases: ['category', 'Category'], example: 'Oils', fallback: 'Dummy' },
  { key: 'categoryId', label: 'categoryId', example: '3' },
  { key: 'typeId', label: 'typeId', example: '2' },
  { key: 'cost', label: 'cost', example: '9.75' },
  { key: 'leadTime', label: 'leadTime', example: '2 weeks' },
];

const normalizeHeader = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const getCellValue = (rawRow: Record<string, any>, column: ExcelImportColumn) => {
  const aliases = [column.key, column.label, ...(column.aliases || [])];
  const rawKeys = Object.keys(rawRow);
  const matchedKey = rawKeys.find((key) => aliases.some((alias) => normalizeHeader(alias) === normalizeHeader(key)));
  return matchedKey ? rawRow[matchedKey] : undefined;
};

const isBlank = (value: any) => value === null || value === undefined || String(value).trim() === '';

const normalizeCellValue = (value: any) => {
  if (isBlank(value)) return null;
  if (value instanceof Date) return value.toISOString();
  return typeof value === 'string' ? value.trim() : value;
};

const getFallback = (column: ExcelImportColumn, rowNumber: number) => {
  if (typeof column.fallback === 'function') {
    return column.fallback(rowNumber);
  }
  if (column.fallback !== undefined) {
    return column.fallback;
  }
  return `Dummy ${column.label} ${rowNumber}`;
};

export const parseExcelFile = async (file: File, schema: ExcelImportColumn[]): Promise<ExcelImportRow[]> => {
  const matrix = (file.name.toLowerCase().endsWith('.csv')
    ? parseCsv(await file.text())
    : await readXlsxFile(file)) as any[][];

  if (matrix.length < 2) return [];

  const headers = matrix[0].map((header) => String(header || ''));
  const rawRows = matrix.slice(1).map((cells) => {
    const row: Record<string, any> = {};
    headers.forEach((header, index) => {
      if (header) row[header] = cells[index] ?? null;
    });
    return row;
  });

  return rawRows.map((rawRow, index) => {
    const rowNumber = index + 2;
    const notes: string[] = [];
    const normalized: ExcelImportRow = {
      __rowNumber: rowNumber,
      __status: 'ready',
      __notes: notes,
    };

    schema.forEach((column) => {
      const value = normalizeCellValue(getCellValue(rawRow, column));

      if (isBlank(value)) {
        if (column.required) {
          normalized[column.key] = getFallback(column, rowNumber);
          notes.push(`${column.label} filled`);
        } else if (column.fallback !== undefined) {
          normalized[column.key] = getFallback(column, rowNumber);
        } else {
          normalized[column.key] = null;
        }
      } else {
        normalized[column.key] = value;
      }
    });

    normalized.__status = notes.length > 0 ? 'filled' : 'ready';
    return normalized;
  });
};

export const exportExcelTemplate = (schema: ExcelImportColumn[], filename: string) => {
  const headers = schema.map((column) => column.label);
  const exampleRow = schema.map((column) => column.example || (column.required ? 'required' : ''));
  const csv = [headers, exampleRow]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.replace(/\.xlsx$/i, '.csv');
  link.click();
  URL.revokeObjectURL(url);
};

const parseCsv = (text: string) => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let index = 0; index < text.length; index++) {
    const char = text[index];
    const nextChar = text[index + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      cell += '"';
      index += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      row.push(cell);
      cell = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }

  if (cell || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  return rows.filter((items) => items.some((item) => item.trim() !== ''));
};
